/** TanStack Query hooks for user profile: fetch, update, avatar upload/delete, and onboarding completion. */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, put, post, del, setUserTimezone } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';
import { useEffect } from 'react';
import type { Profile } from '@/shared/types';

// Re-export type so existing consumers keep working
export type { Profile } from '@/shared/types';

// ─── Query keys ─────────────────────────────────────────────────────────────

export const profileKeys = {
  all: ['f', 'profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useProfile() {
  const query = useQuery({
    queryKey: profileKeys.me(),
    queryFn: () => get<Profile>('/me/profile'),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (query.data) {
      setUserTimezone(query.data.timezone ?? null);
    }
  }, [query.data]);

  return query;
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Profile>) => put<Profile>('/me/profile', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      // Field name `file` matches the upload.single('file') in
      // packages/api/src/routes/profile.routes.ts — and every other
      // multipart upload in the codebase. The previous name `avatar`
      // triggered multer's "Unexpected field" 400.
      formData.append('file', file);
      return post<Profile>('/me/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useDeleteAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => del('/me/profile/avatar'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useCompleteLearnerOnboarding() {
  const queryClient = useQueryClient();
  const { setOnboardingCompleted } = useAuth();

  return useMutation({
    mutationFn: () => put<Profile>('/me/profile', { learnerOnboardingCompleted: true }),
    onSuccess: () => {
      setOnboardingCompleted(true);
      queryClient.setQueryData(profileKeys.me(), (old: Profile | undefined) =>
        old ? { ...old, learnerOnboardingCompleted: true } : old
      );
    },
  });
}
